import React, { useContext, useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'

import FormDialog from '../../components/form-dialog/form-dialog.component'
import ConfirmDialog from '../../components/confirm-dialog/confirm-dialog.component'
import ActionButtons from '../../components/action-buttons/action-buttons.component'
import TableContainer from '../../components/table-container/table-container'
import MultipleSelect from '../../components/multi-select/multi-select'
import ActiveToggle from '../../components/active-toggle/active-toggle.component'
import SearchableInput from '../../components/searchable-input/searchable-input.component'
import ManyToManyInput from '../../components/many-many-input/many-many-input.component'

import AppInsightsTrackingContext from '../../context/app-insights-tracking/AppInsightsTrackingContext'
import { buildPayload, getManyToManyData } from '../../shared/utils/utils'

import RequestService from '../../services/request/request-service'

import {
  ACTIONS_COLUMN,
  BASE_TABLE_COLUMNS,
  DEFAULT_ROW_DATA,
  DEFAULT_TABLE_CONFIGURATION,
} from '../../shared/constants/table'
import { DEFAULT_COMFIRM_INACTIVE_MESSAGE } from '../../shared/constants/messages'
import { POST_METHOD, PUT_METHOD } from '../../shared/constants/requests'
import { OUTLET_QUERY, OUTLETS_SIZES_QUERY } from '../../shared/constants/queries'
import { LOG_PAGE_LOADS } from '../../shared/constants/logging'
import useHttpInactive from '../../shared/hooks/useHttpInactive'
import useLocationQuery from '../../shared/hooks/useLocationQuery'
import useOutletQuery from '../../shared/hooks/useOutletQuery'
import useSizeQuery from '../../shared/hooks/useSizeQuery'

const Outlet = ({ userEmail }) => {
  const history = useHistory()
  const { t } = useTranslation()
  const { trackEvent } = useContext(AppInsightsTrackingContext)

  const [openDialog, setOpen] = useState(false)
  const [openDelDialog, setDelOpen] = useState(false)
  const [openSize, setOpenSize] = useState(false)
  const [loading, setLoading] = useState(false)
  const [modalType, setModalType] = useState('Add new')
  const [rowData, setRowData] = useState(DEFAULT_ROW_DATA)
  const [tableConf, setTableConf] = useState(DEFAULT_TABLE_CONFIGURATION)
  const [filterValue, setFilterValue] = useState(0)
  const [showActive, setShowActive] = useState(true)
  const [selectedSizes, setSelectedSizes] = useState([])

  const outletQuery = useOutletQuery({ showActive, filter: { id_location: filterValue } })
  const locationQuery = useLocationQuery()
  const sizeQuery = useSizeQuery()
  const isLoadingQuery = outletQuery.isFetching || locationQuery.isFetching || sizeQuery.isFetching

  // columns definition for table
  const translatedColumns = BASE_TABLE_COLUMNS.map((column) => ({
    ...column,
    headerName: t(column.headerName),
  }))

  const columns = [
    ...translatedColumns,
    {
      field: 'sizes',
      headerName: t('sizes'),
      flex: 1,
      minWidth: 115,
      renderCell: (params) => (
        <Button
          color="secondary"
          onClick={() => {
            setRowData({ id: params.row.id })
            setOpenSize(true)
          }}
          style={{ textTransform: 'none' }}
        >
          {t('see_sizes')}
        </Button>
      ),
    },
    {
      ...ACTIONS_COLUMN,
      headerName: t(ACTIONS_COLUMN.headerName),
      renderCell: (params) => ActionButtons(params.row, handleClickOpen, handleClickDelOpen),
    },
  ]

  // load location types
  useEffect(() => {
    trackEvent(LOG_PAGE_LOADS.LOAD_OUTLET_PAGE, { email: userEmail })
  }, [])

  // insert and edit
  const insertEditData = async (method, data) => {
    setLoading(true)
    setOpen(false)

    let url = `${OUTLET_QUERY}`
    let response
    try {
      if (method === POST_METHOD) response = await RequestService.Post(url, history, data)
      if (method === PUT_METHOD) response = await RequestService.Put(url, history, data)

      let id_outlet
      if (method === PUT_METHOD) id_outlet = response.data.id
      if (method === POST_METHOD) id_outlet = response.data.id

      const outletsSizesPayload = buildPayload(id_outlet, selectedSizes)
      url = `${OUTLETS_SIZES_QUERY}`
      response = await RequestService.Put(url, history, outletsSizesPayload, { isBlankInstance: true })

      setSelectedSizes([])
      setLoading(false)
      outletQuery.refetch()
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  // Form Dialog handler
  const handleClickOpen = (type, params) => async () => {
    setLoading(true)
    if (type === 'Edit') {
      trackEvent('EDIT_CLICK', { email: userEmail })
      const outletsSizes = await getManyToManyData(`${OUTLETS_SIZES_QUERY}?id=${params.id}`, history, sizeQuery.data)
      setSelectedSizes(outletsSizes)

      setRowData({
        id: params.id,
        name: params.name,
        description: params.description,
        active: params.active,
        id_location: params.id_location,
      })
    } else {
      trackEvent('ADD_CLICK', { email: userEmail })
      setSelectedSizes([])
      setRowData(DEFAULT_ROW_DATA)
    }
    setModalType(type)
    setOpen(true)
    setLoading(false)
  }

  const handleSubmit = (event) => {
    trackEvent('SUBMIT_CLICK', { email: userEmail })
    event.preventDefault()
    const data = { ...rowData }
    const method = modalType !== 'Edit' ? POST_METHOD : PUT_METHOD
    insertEditData(method, data)
  }

  // Confirm Dialog handlers
  const handleClickDelOpen = (params) => () => {
    trackEvent('DELETE_CLICK', { email: userEmail })
    setRowData({
      id: params.id,
      name: params.name,
      description: params.description,
      active: !params.active,
      id_location: params.id_location,
    })
    setDelOpen(true)
  }
  const { executeInactive, loadingInactive } = useHttpInactive(OUTLET_QUERY)
  const onClickDelConfirmation = async () => {
    const { success } = await executeInactive(rowData.id)
    if (success) {
      setDelOpen(false)
      outletQuery.refetch()
    }
  }

  // Form on change
  const handleChange = (event) => {
    const { name, value } = event.target
    setRowData({
      ...rowData,
      [name]: value,
    })
  }

  return (
    <div>
      <Box pt={5} pb={3}>
        <Typography component="h1" variant="h5" color="primary">
          {t('outlet')}
        </Typography>
      </Box>
      <TableContainer
        haveSearchInput="true"
        filterValues={locationQuery.data}
        filterValue={filterValue}
        filter={t('location')}
        filterChange={(event) => {
          setFilterValue(event.target.value)
        }}
        handleClickOpen={() => handleClickOpen('Add new', null)}
        columns={columns}
        rows={outletQuery.data}
        tableConfiguration={tableConf}
        setTableConf={setTableConf}
        onToggleClick={() => setShowActive(!showActive)}
        showActive={showActive}
        loading={isLoadingQuery}
      />
      <MultipleSelect
        open={openSize}
        onClose={() => {
          setOpenSize(false)
        }}
        title={t('sizes')}
        value={sizeQuery.data}
        getRelationUrl="outletssizes"
        mainId={rowData.id}
        lefttoright
      />
      <FormDialog
        title={`${modalType} outlet`}
        open={openDialog}
        onClose={() => {
          setOpen(false)
        }}
        handleSubmit={handleSubmit}
        loading={loading}
      >
        <TextField
          defaultValue={rowData.name}
          autoFocus
          variant="outlined"
          margin="normal"
          name="name"
          label={t('name')}
          type="text"
          fullWidth
          onChange={handleChange}
          required
        />
        <TextField
          defaultValue={rowData.description}
          variant="outlined"
          margin="normal"
          name="description"
          label={t('description')}
          type="text"
          fullWidth
          onChange={handleChange}
        />
        <SearchableInput
          value={locationQuery.data.find((gr) => gr.id === rowData.id_location) ?? 0}
          options={locationQuery.data}
          onChange={(event) => handleChange(event)}
          label={t('location')}
          id="id_location"
          name="id_location"
        />
        <ManyToManyInput
          inputLabel={t('sizes')}
          name="id_size"
          selectedValues={selectedSizes}
          allValues={sizeQuery.data}
          setSelectedValues={setSelectedSizes}
        />
        {modalType === 'Edit' && (
          <ActiveToggle value={rowData.active} onToggle={() => setRowData({ ...rowData, active: !rowData.active })} />
        )}
      </FormDialog>
      <ConfirmDialog
        title={t('deactivate_outlet')}
        okButton="Deactivate"
        open={openDelDialog}
        loading={loadingInactive}
        onClose={() => {
          setDelOpen(false)
        }}
        onOk={onClickDelConfirmation}
      >
        {t(DEFAULT_COMFIRM_INACTIVE_MESSAGE)} {rowData.name}?
      </ConfirmDialog>
    </div>
  )
}

const mapStateToProps = (state) => ({
  userEmail: state.user.email,
})

export default connect(mapStateToProps, null)(Outlet)
